/**
 * SubscriptionBanner — persistent top-of-app banner for employers whose
 * subscription is past_due / in grace / suspended.
 *
 * Fetches /api/invoices/subscription/status once per app load (cheap — a
 * single doc read). Renders nothing when status is active / trialing.
 *
 * Colour language:
 *   grace     → amber, ticking countdown, CTA "Pay now"
 *   overdue   → red, CTA "Pay now"
 *   suspended → hard red, CTA "Pay now — features unlocked instantly"
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, CreditCard, Zap } from "lucide-react";

export default function SubscriptionBanner() {
    const { user } = useAuth() || {};
    const [status, setStatus] = useState(null);

    useEffect(() => {
        if (!user?.id) { setStatus(null); return; }
        // Only employers/recruiters see the banner. Admin (Portalwiz staff) has
        // its own dashboard for billing hygiene.
        if (!["employer", "recruiter"].includes(user.role)) return;
        let cancel = false;
        api.get("/invoices/subscription/status")
            .then(({ data }) => { if (!cancel) setStatus(data); })
            .catch(() => {});
        return () => { cancel = true; };
    }, [user?.id, user?.role]);

    if (!status || !["grace", "past_due", "overdue", "suspended"].includes(status.status)) return null;

    const config = {
        grace: {
            cls: "bg-amber-500 text-black",
            Icon: AlertTriangle,
            label: "Payment past due",
            msg: `You have ${status.outstanding_count} outstanding invoice${status.outstanding_count === 1 ? "" : "s"} totalling ${status.outstanding_total?.toLocaleString()} — ${status.grace_days_remaining ?? 0} days left in your grace window before features are suspended.`,
            cta: "Pay now",
        },
        past_due: {
            cls: "bg-amber-500 text-black",
            Icon: AlertTriangle,
            label: "Payment past due",
            msg: `You have ${status.outstanding_count} unpaid invoice${status.outstanding_count === 1 ? "" : "s"} totalling ${status.outstanding_total?.toLocaleString()}. Settle to keep your account active.`,
            cta: "Pay now",
        },
        overdue: {
            cls: "bg-amber-500 text-black",
            Icon: AlertTriangle,
            label: "Invoice overdue",
            msg: `Your invoice is past due. Please settle to avoid suspension.`,
            cta: "Pay now",
        },
        suspended: {
            cls: "bg-rose-600 text-white",
            Icon: Zap,
            label: "Account suspended",
            msg: `Writes are blocked until you settle the outstanding balance. You can still view + export your data.`,
            cta: "Pay now — instant reactivation",
        },
    }[status.status];

    if (!config) return null;
    const { Icon } = config;
    return (
        <div className={`${config.cls} px-4 py-2 flex items-center gap-3 justify-center sticky top-0 z-40`} data-testid="subscription-banner">
            <Icon className="w-4 h-4 shrink-0" />
            <div className="flex-1 min-w-0">
                <span className="text-[10px] font-mono uppercase tracking-widest font-bold mr-2">{config.label}</span>
                <span className="text-xs">{config.msg}</span>
            </div>
            <Link
                to="/billing"
                className="inline-flex items-center gap-1.5 h-7 px-3 bg-black text-white text-[10px] font-mono uppercase tracking-widest hover:bg-black/80 shrink-0"
                data-testid="subscription-banner-cta"
            >
                <CreditCard className="w-3 h-3" /> {config.cta}
            </Link>
        </div>
    );
}
